import CryptoJS from 'crypto-js'
import cookie from 'cookiejs'

declare global {
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type
  interface Window {
    $chatwoot: any
    chatwootSDK: any
    chatwootSettings: any
    writer: any
  }
}

const chatWootToken = import.meta.env.VITE_APP_CHATWOOT_TOKEN
const chatWootScript = `window.chatwootSettings = {
  hideMessageBubble: false,
};
(function(d,t) {
  var BASE_URL="https://app.chatwoot.com";
  var g=d.createElement(t),s=d.getElementsByTagName(t)[0];
  g.src=BASE_URL+"/packs/js/sdk.js?t=${new Date().getTime()}";
  s.parentNode.insertBefore(g,s);
  g.onload=function(){
    window.chatwootSDK.run({
      websiteToken: '${chatWootToken}',
      baseUrl: BASE_URL
    })
  }
})(document,"script");`
const CHATWOOT_SCRIPT_ID = 'chatWootScript'

const placeScript = (scriptText: string, id: string): void => {
  if (document.getElementById(id)) {
    removeChatWootScript()
  }
  const script = document.createElement('script')
  const inlineScript = document.createTextNode(scriptText)

  script.setAttribute('type', 'text/javascript')
  script.setAttribute('language', 'javascript')
  script.setAttribute('id', id)

  script.appendChild(inlineScript)
  const head = document.getElementsByTagName('head')
  if (head.length) {
    head[0].appendChild(script)
  }
}

function changeElementVisibility(element: string, style: string): void {
  if (document.getElementsByClassName(element)?.length) {
    ;(document.getElementsByClassName(element)[0] as HTMLElement).style.cssText = `display: ${style}`
  }
}

export const hideChatWoot = (): void => {
  const hideUsingAPI = (): void => {
    if (window.$chatwoot) {
      if (typeof window.$chatwoot.toggle === 'function') {
        const widgetHolder = document.getElementsByClassName('woot-widget-holder')
        if (widgetHolder?.length) {
          const widgetStyle = window.getComputedStyle(widgetHolder[0] as Element)
          const isWidgetOpen = widgetStyle.display !== 'none' && widgetStyle.visibility !== 'hidden'
          if (isWidgetOpen) {
            window.$chatwoot.toggle()
          }
        }
      }
      if (typeof window.$chatwoot.toggleBubbleVisibility === 'function') {
        window.$chatwoot.toggleBubbleVisibility('hide')
      }
    }
  }
  
  if (window.$chatwoot) {
    hideUsingAPI()
    return
  }
  
  window.addEventListener("chatwoot:ready", () => {
    hideUsingAPI()
  }, { once: true })
}

export const showChatWoot = (): void => {
  const showUsingAPI = (): void => {
    if (window.$chatwoot) {
      if (typeof window.$chatwoot.toggleBubbleVisibility === 'function') {
        window.$chatwoot.toggleBubbleVisibility('show')
      }
      if (typeof window.$chatwoot.toggle === 'function') {
        window.$chatwoot.toggle()
      }
    }
  }
  
  if (window.$chatwoot) {
    showUsingAPI()
    return
  }
  
  window.addEventListener("chatwoot:ready", () => {
    showUsingAPI()
  }, { once: true })
}

export const isChatWootVisible = (): boolean => {
  const widgetHolder = document.getElementsByClassName('woot-widget-holder')
  const bubbleHolder = document.getElementsByClassName('woot--bubble-holder')
  
  if (widgetHolder?.length) {
    const widgetStyle = window.getComputedStyle(widgetHolder[0] as Element)
    const isWidgetVisible = widgetStyle.display !== 'none' && widgetStyle.visibility !== 'hidden'
    
    if (bubbleHolder?.length) {
      const bubbleStyle = window.getComputedStyle(bubbleHolder[0] as Element)
      const isBubbleVisible = bubbleStyle.display !== 'none' && bubbleStyle.visibility !== 'hidden'
      return isWidgetVisible || isBubbleVisible
    }
    
    return isWidgetVisible
  }
  return false
}

export const toggleChatWoot = (): void => {
  if (isChatWootVisible()) {
    hideChatWoot()
  } else {
    showChatWoot()
  }
}

type ChatWootUser = {
  email: string
  accountId: string | number
  firstName: string
  lastName: string
  jobTitle?: string
  phone?: string
}

export const addChatWootScript = async (user: ChatWootUser): Promise<void> => {
  // In the future, we will likely turn this back on for
  // suppliers only, so leaving the condition commented.
  // if (this.isStandart)
  if (user && window.$chatwoot?.user?.email !== user.email) {
    // eslint-disable-next-line no-console
    await placeScript(chatWootScript, CHATWOOT_SCRIPT_ID)
    if (window.$chatwoot) {
      window.$chatwoot.reset()
      changeElementVisibility('woot-widget-holder', 'block')
      changeElementVisibility('woot--bubble-holder', 'block')
    }

    window.addEventListener('chatwoot:ready', () => {
      const userId = user.accountId

      const userIdentifier = import.meta.env.VITE_APP_ENV === 'production'
        ? `production:${userId}`
        : `staging:${userId}`

      const chatWootIdentity = import.meta.env.VITE_APP_CHATWOOT_USER_IDENTITY
      const hash = CryptoJS.HmacSHA256(userIdentifier, chatWootIdentity).toString()
      const chatwootCockiesList = Object.keys(cookie() as Record<string, any>)
        .filter((key: string) => key.startsWith('cw_user'))

      cookie.remove(...chatwootCockiesList)
      window.$chatwoot.setUser(userIdentifier, {
        id: userId,
        email: String(user.email),
        name: String(`${user.firstName} ${user.lastName}`),
        identifier_hash: hash,
      })

      window.$chatwoot.setCustomAttributes({
        jobTitle: user.jobTitle,
        phone: user.phone,
      })

      const browserLocale = navigator.language || (navigator as any).userLanguage

      const iso639LanguageCode = browserLocale.split('-')[0]
      const supportedLanguages = [
        'en',
        'ar',
        'nl',
        'fr',
        'de',
        'hi',
        'it',
        'ja',
        'ko',
        'pt',
        'ru',
        'zh',
        'es',
        'ml',
        'ca',
        'el',
        'pt_BR',
        'ro',
        'ta',
        'fa',
        'zh_TW',
        'vi',
        'da',
        'tr',
        'cs',
        'fi',
        'id',
        'sv',
        'hu',
        'no',
        'zh_CN',
        'pl',
      ]
      window.$chatwoot.setLocale(
        supportedLanguages.includes(iso639LanguageCode)
          ? iso639LanguageCode
          : 'en',
      )
      
      if (window.$chatwoot && typeof window.$chatwoot.toggleBubbleVisibility === 'function') {
        window.$chatwoot.toggleBubbleVisibility('hide')
      }

      const closeButton = document.querySelector('.woot--close')
      if (closeButton && window.$chatwoot) {
        closeButton.addEventListener('click', () => {
          if (window.$chatwoot && typeof window.$chatwoot.toggleBubbleVisibility === 'function') {
            window.$chatwoot.toggleBubbleVisibility('hide')
          }
        })
      }
    }, { once: true })
  }
}

export const removeChatWootScript = (): void => {
  const existScript = document.getElementById(CHATWOOT_SCRIPT_ID)
  if (existScript) {
    document.getElementById(CHATWOOT_SCRIPT_ID)!.remove()
    changeElementVisibility('woot-widget-holder', 'none')
    changeElementVisibility('woot--bubble-holder', 'none')
    if (window?.$chatwoot?.user) {
      delete window.$chatwoot.user
    }
  }
}
